import React, { useState } from 'react';
import { InfoIcon } from 'lucide-react';
import type { BuilderController } from './useBuilder';
import { BuilderChat } from './BuilderChat';
import { BuilderCanvas } from './BuilderCanvas';
import { BuilderPreview } from './BuilderPreview';
import { cn } from '../../../src/lib/utils';
type Panel = 'build' | 'canvas' | 'preview';
const PANELS: {
  id: Panel;
  label: string;
}[] = [{
  id: 'build',
  label: 'Build'
}, {
  id: 'canvas',
  label: 'Workflow'
}, {
  id: 'preview',
  label: 'Preview'
}];

/** Three-panel DGPT Builder mockup. On small screens one panel shows at a time. */
export function BuilderApp({
  builder
}: {
  builder: BuilderController;
}) {
  const [panel, setPanel] = useState<Panel>('build');
  return <div className="overflow-hidden rounded-2xl border border-night-line bg-night-2 shadow-[0_50px_120px_-60px_rgba(124,92,255,0.55)]" data-id="element-109" data-mp="zb7yhfywp6qd">
      <div className="flex items-center gap-3 border-b border-night-line bg-night px-4 py-2.5" data-id="element-110" data-mp="149sybea8xoea">
        <span className="flex gap-1.5" aria-hidden="true" data-id="element-111" data-mp="18auybxbzm4hb">
          <span className="h-2.5 w-2.5 rounded-full bg-night-line" data-id="element-112" data-mp="2yr4d8ubhxdmo" />
          <span className="h-2.5 w-2.5 rounded-full bg-night-line" data-id="element-113" data-mp="2tqqfq67itm2w" />
          <span className="h-2.5 w-2.5 rounded-full bg-night-line" data-id="element-114" data-mp="2ttme1vna6crv" />
        </span>
        <p className="min-w-0 flex-1 truncate text-[12px] text-night-muted" data-id="element-115" data-mp="2osf4br1kv3ik">
          <span className="font-semibold text-night-text" data-id="element-116" data-mp="2judmf9fw1a8o">DGPT Builder</span> · {builder.template.name}
        </p>
        <span className="shrink-0 rounded-md border border-violet/40 bg-violet/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-violet-ink" data-id="element-117" data-mp="3hg2mz0jafk2k">
          Interactive preview
        </span>
      </div>

      <div role="tablist" aria-label="Builder panels" className="grid grid-cols-3 gap-1 border-b border-night-line p-1.5 lg:hidden" data-id="element-118" data-mp="37ooieechbu2k">
        {PANELS.map(item => <button key={item.id} type="button" role="tab" aria-selected={panel === item.id} onClick={() => setPanel(item.id)} className={cn('min-h-[40px] rounded-lg text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet', panel === item.id ? 'bg-night-raised text-night-text' : 'text-night-faint')} data-id="element-119" data-mp="43ipzx98pazk">
            {item.label}
          </button>)}
      </div>

      <div className="grid h-[620px] lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)_minmax(0,360px)]" data-id="element-120" data-mp="1aslic0eha9km">
        <div className={cn('min-h-0 border-night-line lg:block lg:border-r', panel === 'build' ? 'block' : 'hidden')} data-id="element-121" data-mp="1eu7285pfjo4b">
          <BuilderChat builder={builder} data-id="element-122" data-mp="10uxmf754yw0v" />
        </div>
        <div className={cn('min-h-0 border-night-line lg:block lg:border-r', panel === 'canvas' ? 'block' : 'hidden')} data-id="element-123" data-mp="10wihqyz9yizs">
          <BuilderCanvas builder={builder} data-id="element-124" data-mp="2v8fd330u8904" />
        </div>
        <div className={cn('min-h-0 lg:block', panel === 'preview' ? 'block' : 'hidden')} data-id="element-125" data-mp="2v8ykvqwrxh0f">
          <BuilderPreview builder={builder} data-id="element-126" data-mp="3067660d7y15w" />
        </div>
      </div>

      <p className="flex items-start gap-2 border-t border-night-line bg-night px-4 py-2.5 text-[11px] text-night-faint" data-id="element-127" data-mp="ojn5u6ebjwsn">
        <InfoIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-128" data-mp="jmekxy1sdenp" />
        Interactive preview with scripted responses and fictional data. Nothing is saved, and no models or data sources are connected.
      </p>
    </div>;
}